import React, { PureComponent } from 'react';
import { v4 as uuid } from 'uuid';
import PropTypes from 'prop-types';
import { debounce, map } from 'lodash';
import { MuiThemeProvider, withStyles } from '@material-ui/core/styles';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Typography from '@material-ui/core/Typography';
import { muiThemeLuma } from 'embeds/themes';

import { OldTextFieldSearch } from '../OldTextFieldSearch';
import MenuContainer from '../MenuContainer';

const styles = (theme) => {
  const {
    custom: {
      palette: { colorDarkCyan, colorGrayishBlue, colorSupportSlate },
      fontSizes,
    },
    palette: { primary },
    spacing: { unit },
  } = theme;

  return {
    filterField: {
      border: `1px solid ${colorGrayishBlue} !important`,
      borderRadius: unit / 2,
    },
    filterFieldInput: {
      borderRadius: unit / 2,
      fontSize: fontSizes.small,
      height: '36px !important',
      padding: 0,
      paddingLeft: 8,
      '&::placeholder': {
        color: `${colorSupportSlate} !important`,
      },
    },
    listItem: {
      padding: 0,
    },
    menuItem: {
      height: 'auto',
      padding: `${unit}px ${unit * 2}px `,
    },
    subtext: {
      color: colorDarkCyan,
      fontFamily: 'roboto mono',
      fontSize: fontSizes.tiny,
      fontWeight: 500,
      whiteSpace: 'normal',
    },
    text: {
      color: primary.main,
      fontSize: fontSizes.body,
      fontWeight: 600,
      lineHeight: 'normal',
      whiteSpace: 'normal',
    },
  };
};

class SquigglyTemplatesMenu extends PureComponent {
  constructor(props) {
    super(props);

    this.renderMenuItems = this.renderMenuItems.bind(this);

    this.state = {
      filtering: false,
    };
  }

  handleFilter = (value) => {
    const { onFilter } = this.props;

    this.setState({ filtering: Boolean(value) });

    onFilter(value);
  };

  renderSearchField() {
    const { classes, onFilter } = this.props;

    if (!onFilter) return;

    const slowSearch = debounce(this.handleFilter, 300);

    return (
      <ListItem className={classes.menuItem}>
        <OldTextFieldSearch
          autoFocus
          InputProps={{
            classes: {
              root: classes.filterField,
              input: classes.filterFieldInput,
            },
            endAdornment: null,
          }}
          onChange={slowSearch}
          placeholder="Find a squiggly"
          size="small"
        />
      </ListItem>
    );
  }

  renderMenuItems(item) {
    const { classes, onSelect } = this.props;

    return (
      <MenuItem className={classes.menuItem} key={`${item.id}-${uuid()}`} onClick={() => onSelect(item)}>
        <ListItemText
          classes={{ root: classes.listItem, primary: classes.text, secondary: classes.subtext }}
          primary={item.name}
          secondary={item.id}
        />
      </MenuItem>
    );
  }

  renderEmptyMessage() {
    const { classes, squigglyTemplates } = this.props;
    const { filtering } = this.state;

    if (squigglyTemplates.length > 0) return;

    return (
      <ListItem className={classes.menuItem}>
        <Typography>{filtering ? 'No results found' : 'No squigglies created'}</Typography>
      </ListItem>
    );
  }

  render() {
    const { menuAnchor, menuClassName, onClickAway, placement, squigglyTemplates } = this.props;

    return (
      <MuiThemeProvider theme={muiThemeLuma}>
        <MenuContainer
          className={menuClassName}
          menuAnchor={menuAnchor}
          onClickAway={onClickAway}
          placement={placement || 'bottom'}
        >
          {this.renderSearchField()}
          <MenuList disablePadding>
            {this.renderEmptyMessage()}
            {map(squigglyTemplates, this.renderMenuItems)}
          </MenuList>
        </MenuContainer>
      </MuiThemeProvider>
    );
  }
}

SquigglyTemplatesMenu.propTypes = {
  classes: PropTypes.object.isRequired,
  menuAnchor: PropTypes.object,
  menuClassName: PropTypes.string,
  onClickAway: PropTypes.func.isRequired,
  onFilter: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  placement: PropTypes.string,
  squigglyTemplates: PropTypes.array.isRequired,
};

export default withStyles(styles)(SquigglyTemplatesMenu);
